import { useState, useEffect, useCallback } from 'react';
import { Spinner, Modal, ModalHeader, ModalBody } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { requestPublicPreviewUrl } from '../Generate/Components/FormikHelpers';
import { toast } from 'react-toastify';
import { Previewer } from '../../../../components/DocumentPreviewer/Previewer';
import '../Stylesheets/incident-report-public.scss';

function decodeTokenPayload(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function derivePermissions(token, serverCanViewInvestigation, allPersons = []) {
  const payload = decodeTokenPayload(token);
  console.log('[derivePermissions] Token Payload:', payload);

  if (!payload || payload.purpose !== 'preview') {
    console.log('[derivePermissions] Access Denied: Missing or invalid token purpose.');
    return { canView: false, canViewInvestigation: false, personsToShow: 'all', matchedIdx: -1 };
  }

  const FULL_ACCESS_ROLES = ['admin', 'company', 'manager', 'technical staff'];
  const role = payload.role || null;
  const recipientEmail = payload.recipientEmail || null;

  // 1. Identify if this person is listed as involved in the incident.
  const matchedIdx = allPersons.findIndex(
    (p) => p.email?.toLowerCase() === recipientEmail?.toLowerCase()
  );
  const isPersonInvolved = matchedIdx >= 0;

  console.log('[derivePermissions] Context:', {
    role,
    recipientEmail,
    isPersonInvolved,
    matchedIdx,
    serverCanViewInvestigation,
  });

  // 2. Strict Constraint: Persons involved NEVER see the investigation.
  if (isPersonInvolved) {
    console.log('[derivePermissions] Restricted: Person involved cannot view investigation.');
    return {
      canView: true,
      canViewInvestigation: false,
      personsToShow: 'matched',
      matchedIdx,
    };
  }

  // 3. Authorization for non-involved parties (Managers, Authorized Recipients, etc.)
  // We trust the serverCanViewInvestigation prop if available (as the server already verified the token).
  // Otherwise, we fallback to checking the token's role.
  let canViewInvestigation = false;
  if (typeof serverCanViewInvestigation === 'boolean') {
    canViewInvestigation = serverCanViewInvestigation;
  } else {
    const hasFullAccessRole = role && FULL_ACCESS_ROLES.includes(role);
    const isNotificationRecipient = role === 'notification_recipient';
    canViewInvestigation = hasFullAccessRole || isNotificationRecipient;
  }

  const result = {
    canView: true,
    canViewInvestigation,
    personsToShow: 'all',
    matchedIdx: -1,
  };

  console.log('[derivePermissions] Final Result:', result);
  return result;
}

const PERSON_TYPE_LABELS = {
  employee: 'Employee',
  tenant: 'Tenant',
  contractor: 'Contractor',
  other: 'Other',
};

const formatLabel = (value) => {
  if (!value) return null;
  return value.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const fmt = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(d);
  } catch { return null; }
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
  } catch { return null; }
};

const display = (v) => (v === null || v === undefined || v === '') ? null : String(v);
const fileName = (k) => (typeof k === 'string' ? k.split('/').pop() : '');
const isVideoKey = (k) => /\.(mp4|mov|webm|avi|mkv)$/i.test(k);
const isPdfKey = (k) => /\.pdf$/i.test(k);
const isImageKey = (k) => /\.(jpe?g|png|gif|webp)$/i.test(k);

/* ─────────────────────────────────────────
   PREVIEW FIELD  — uses pir-preview-* classes
   ───────────────────────────────────────── */
const PreviewField = ({ label, value, span = 1, full, colSpan }) => {
  const isEmpty = !value || value === '—';
  const gridCol = full ? '1 / -1' : colSpan ? `span ${colSpan}` : span === 2 ? 'span 2' : 'span 1';
  return (
    <div
      className="ppir-preview-field"
      style={{
        gridColumn: gridCol,
        padding: '12px 18px',
        background: '#fff',
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--pir-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 600,
          color: isEmpty ? 'var(--pir-muted)' : 'var(--pir-text-body)',
          lineHeight: 1.45,
          fontStyle: isEmpty ? 'italic' : 'normal',
        }}
      >
        {isEmpty ? '—' : value}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   PREVIEW SECTION
   cols prop sets the grid column count (default 2)
   ───────────────────────────────────────── */
const PreviewSection = ({ icon, title, children, green, cols = 2 }) => (
  <div
    style={{
      background: '#fff',
      border: '1.5px solid var(--pir-violet-bd)',
      borderRadius: 14,
      marginBottom: 16,
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(22,59,95,0.06)',
    }}
  >
    {/* Section title bar */}
    <div
      style={{
        padding: '10px 16px',
        background: green ? 'rgba(5,150,105,0.06)' : 'var(--pir-violet-md)',
        borderBottom: '1.5px solid var(--pir-violet-bd)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 11,
        fontWeight: 800,
        color: green ? '#065f46' : 'var(--pir-indigo)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      <FeatherIcon icon={icon} size={13} />
      <span>{title}</span>
    </div>

    {/* Children — rendered in a grid; each PreviewField controls its own column span */}
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: '1px',
        background: 'var(--pir-violet-bd)',
      }}
    >
      {children}
    </div>
  </div>
);

/* ─────────────────────────────────────────
   ATTACHMENT THUMBNAIL
   ───────────────────────────────────────── */
const AttachmentThumbnail = ({ fileKey, token, onClick }) => {
  const [thumbUrl, setThumbUrl] = useState(null);
  const isImage = isImageKey(fileKey);

  useEffect(() => {
    if (!isImage || !token || !fileKey || fileKey === 'N/A') return;
    requestPublicPreviewUrl({ token, key: fileKey })
      .then((data) => setThumbUrl(data.presignedUrl))
      .catch(() => {});
  }, [isImage, token, fileKey]);

  if (fileKey === 'N/A') {
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          background: '#eff6ff',
          border: '1.5px solid #93c5fd',
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 700,
          color: '#2563eb',
        }}
      >
        <FeatherIcon icon="slash" size={12} color="#2563eb" />
        N/A
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      title={fileName(fileKey)}
      style={{
        width: 80,
        height: 80,
        borderRadius: 10,
        border: '1.5px solid #c2d5e3',
        overflow: 'hidden',
        cursor: 'pointer',
        background: '#eef3f7',
        position: 'relative',
        flexShrink: 0,
        transition: 'transform 0.15s, box-shadow 0.15s',
        boxShadow: '0 2px 8px rgba(22,59,95,0.08)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 18px rgba(22,59,95,0.18)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(22,59,95,0.08)';
      }}
    >
      {isImage ? (
        thumbUrl ? (
          <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Spinner size="sm" color="secondary" />
          </div>
        )
      ) : isVideoKey(fileKey) ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 4 }}>
          <FeatherIcon icon="video" size={22} color="#163b5f" />
          <span style={{ fontSize: 9, color: '#6a8ca8', fontWeight: 600, textTransform: 'uppercase' }}>Video</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 4 }}>
          <FeatherIcon icon="file-text" size={22} color="#163b5f" />
          <span style={{ fontSize: 9, color: '#6a8ca8', fontWeight: 600, textTransform: 'uppercase' }}>PDF</span>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────
   MEDIA PREVIEW MODAL
   ───────────────────────────────────────── */
const MediaPreviewModal = ({ isOpen, toggle, media, onPrev, onNext }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
      if (e.key === 'Escape') toggle();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onPrev, onNext, toggle]);

  if (!media || !isOpen) return null;

  const getExt = (k) => (typeof k === 'string' ? k.split('.').pop()?.toLowerCase() : '');

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = media.url;
    a.download = media.name || 'document';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" backdrop="static" style={{ zIndex: 9999 }}>
      <ModalHeader
        toggle={toggle}
        style={{
          background: 'linear-gradient(135deg, #0b1d31 0%, #163b5f 60%, #eef3f7 100%)',
          border: 'none',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
          <span style={{ color: '#fff', fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.2)', fontSize: 14 }}>
            {media.isVideo ? '🎬' : media.isPdf ? '📄' : '🖼'} {media.name || 'Preview'}
          </span>
          <button
            onClick={handleDownload}
            style={{
              marginLeft: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 14px',
              background: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 8,
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <FeatherIcon icon="download" size={13} /> Download
          </button>
        </div>
      </ModalHeader>
      <ModalBody className="p-0" style={{ background: '#f8fafc', position: 'relative', minHeight: 300 }}>
        {onPrev && (
          <button
            onClick={onPrev}
            style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              zIndex: 10, background: 'rgba(0,0,0,0.55)', color: '#fff',
              border: 'none', borderRadius: '50%', width: 44, height: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            <FeatherIcon icon="chevron-left" size={20} />
          </button>
        )}
        {onNext && (
          <button
            onClick={onNext}
            style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              zIndex: 10, background: 'rgba(0,0,0,0.55)', color: '#fff',
              border: 'none', borderRadius: '50%', width: 44, height: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            <FeatherIcon icon="chevron-right" size={20} />
          </button>
        )}
        <div style={{ padding: '16px 56px' }}>
          <Previewer signedUrl={media.url} type={getExt(media.name)} url={media.name} height="70vh" width="100%" />
        </div>
      </ModalBody>
    </Modal>
  );
};

/* ─────────────────────────────────────────
   SINGLE DOC PREVIEW FIELD
   ───────────────────────────────────────── */
const SingleDocPreviewField = ({ label, fileKey, token, onClick }) => {
  if (!fileKey || fileKey === 'N/A') {
    return <PreviewField label={label} value={fileKey === 'N/A' ? 'N/A' : null} />;
  }
  return (
    <div style={{ padding: '12px 16px', background: '#fff' }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--pir-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          padding: '7px 10px',
          borderRadius: 8,
          background: 'rgba(22,59,95,0.05)',
          border: '1px solid rgba(22,59,95,0.15)',
          transition: 'all 0.15s',
        }}
      >
        <FeatherIcon
          icon={isPdfKey(fileKey) ? 'file-text' : isVideoKey(fileKey) ? 'video' : isImageKey(fileKey) ? 'image' : 'file'}
          size={14}
          color="#163b5f"
        />
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#163b5f',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {fileName(fileKey)}
        </span>
        <FeatherIcon icon="eye" size={13} color="#3a6b98" />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   PERSON CARD
   ───────────────────────────────────────── */
const PersonCard = ({ person, index, token, onPreviewMedia }) => {
  const typeDisplay = person.type
    ? person.type === 'other' && person.specifyType
      ? `Other (${person.specifyType})`
      : PERSON_TYPE_LABELS[person.type] || formatLabel(person.type)
    : null;

  const fullName = [person.firstName, person.lastName].filter(Boolean).join(' ');

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 18,
        border: '1.5px solid var(--pir-violet-bd)',
        marginBottom: 20,
        boxShadow: 'var(--pir-shadow)',
        overflow: 'hidden',
      }}
    >
      {/* Card header — premium gradient */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '18px 22px',
          background: 'linear-gradient(135deg, #061524 0%, #163b5f 60%, #3a6b98 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative glows */}
        <div
          style={{
            position: 'absolute', top: '-50px', right: '-20px',
            width: 160, height: 160, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(37,99,235,0.22) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        {/* Avatar */}
        <div
          style={{
            width: 46, height: 46, borderRadius: 14, flexShrink: 0,
            background: 'linear-gradient(135deg,rgba(255,255,255,0.2),rgba(255,255,255,0.08))',
            border: '1.5px solid rgba(255,255,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 800, fontSize: 18,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2),inset 0 1px 0 rgba(255,255,255,0.2)',
            position: 'relative', zIndex: 1,
          }}
        >
          {fullName.charAt(0) || <FeatherIcon icon="user" size={18} color="#fff" />}
        </div>

        {/* Name + type */}
        <div style={{ flex: 1, position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', lineHeight: 1.3, letterSpacing: '-0.2px' }}>
            {fullName || `Person ${index + 1}`}
          </div>
          {typeDisplay && (
            <div
              style={{
                display: 'inline-flex', alignItems: 'center',
                fontSize: 10.5, color: 'rgba(96,165,250,0.9)', fontWeight: 700,
                marginTop: 4, background: 'rgba(37,99,235,0.2)',
                border: '1px solid rgba(96,165,250,0.3)',
                padding: '2px 10px', borderRadius: 20,
                letterSpacing: '0.4px', textTransform: 'uppercase',
              }}
            >
              {typeDisplay}
              {person.email ? ` · ${person.email}` : ''}
            </div>
          )}
        </div>
      </div>

      {/* Card body */}
      <div style={{ padding: '20px 22px', background: '#f6f9fc', display: 'flex', flexDirection: 'column', gap: 0 }}>

        {/* Personal Information — 3-column grid
            Row 1: First Name | Middle Name | Last Name
            Row 2: Person Type | Email Address | Phone Number(s)
            Row 3: Unit No. | Home Address (spans 2)
        */}
        <PreviewSection icon="user" title="Personal Information" cols={3}>
          {/* Row 1 */}
          <PreviewField label="First Name"   value={display(person.firstName)}  />
          <PreviewField label="Middle Name"  value={display(person.middleName)} />
          <PreviewField label="Last Name"    value={display(person.lastName)}   />
          {/* Row 2 */}
          <PreviewField label="Person Type"  value={typeDisplay} />
          <PreviewField label="Email Address" value={display(person.email)} />
          <PreviewField
            label="Phone Number(s)"
            value={display(
              Array.isArray(person.phoneNumbers) && person.phoneNumbers.filter(Boolean).length > 0
                ? person.phoneNumbers.filter(Boolean).join(', ')
                : person.phoneNo
            )}
          />
          {/* Row 3 */}
          <PreviewField label="Unit No."     value={display(person.unitNo)} />
          <PreviewField
            label="Home Address"
            value={display(person.homeAddress || person.address)}
            colSpan={2}
          />
        </PreviewSection>

        {/* Incident Details */}
        <PreviewSection icon="file-text" title="Incident Details">
          <PreviewField
            label="Notification to Property Manager"
            value={fmt(person.notificationToPropertyManagerDateTime)}
            full
          />
          <PreviewField label="Statement" value={display(person.statement)} full />
        </PreviewSection>

        {/* Attachments */}
        {person.attachments?.length > 0 && (
          <div
            style={{
              background: '#fff',
              border: '1.5px solid var(--pir-violet-bd)',
              borderRadius: 14,
              marginBottom: 16,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(22,59,95,0.06)',
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                background: 'var(--pir-violet-md)',
                borderBottom: '1.5px solid var(--pir-violet-bd)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 11, fontWeight: 800, color: 'var(--pir-indigo)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}
            >
              <FeatherIcon icon="paperclip" size={13} />
              <span>Attachments ({person.attachments.length})</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: 16 }}>
              {person.attachments.map((key, ai) => (
                <AttachmentThumbnail
                  key={ai}
                  fileKey={key}
                  token={token}
                  onClick={() => onPreviewMedia(key, person.attachments, ai)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Witnesses */}
        {(person.witnessChoice === 'no' || (person.witnesses?.length === 0 && person.witnessChoice !== 'yes')) ? (
          <div
            style={{
              background: '#fff',
              border: '1.5px solid var(--pir-violet-bd)',
              borderRadius: 14,
              marginBottom: 16,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(22,59,95,0.06)',
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                background: 'var(--pir-violet-md)',
                borderBottom: '1.5px solid var(--pir-violet-bd)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 11, fontWeight: 800, color: 'var(--pir-indigo)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}
            >
              <FeatherIcon icon="users" size={13} />
              <span>Witnesses</span>
            </div>
            <div style={{ padding: '14px 18px', fontSize: 12.5, color: '#6a8ca8', fontStyle: 'italic' }}>
              No witnesses reported
            </div>
          </div>
        ) : person.witnesses?.length > 0 ? (
          <div
            style={{
              background: '#fff',
              border: '1.5px solid var(--pir-violet-bd)',
              borderRadius: 14,
              marginBottom: 16,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(22,59,95,0.06)',
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                background: 'var(--pir-violet-md)',
                borderBottom: '1.5px solid var(--pir-violet-bd)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 11, fontWeight: 800, color: 'var(--pir-indigo)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}
            >
              <FeatherIcon icon="users" size={13} />
              <span>Witnesses ({person.witnesses.length})</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {person.witnesses.map((w, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '11px 18px',
                    borderTop: i === 0 ? 'none' : '1px solid var(--pir-violet-bd)',
                    background: '#fff',
                  }}
                >
                  <div
                    style={{
                      width: 26, height: 26, borderRadius: '50%',
                      background: 'linear-gradient(135deg,#163b5f,#3a6b98)',
                      color: '#fff', fontSize: 11, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13.5 }}>
                      {[w.firstName, w.lastName].filter(Boolean).join(' ') || `Witness ${i + 1}`}
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 3, fontSize: 11.5, color: '#64748b', flexWrap: 'wrap' }}>
                      {w.email && <span>✉ {w.email}</span>}
                      {w.phoneNo && <span>📞 {w.phoneNo}</span>}
                      {w.address && <span>📍 {w.address}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Digital Signature */}
        {person.signature?.signature && (
          <div
            style={{
              background: '#fff',
              border: '1.5px solid rgba(52,211,153,0.4)',
              borderRadius: 14,
              marginBottom: 16,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(5,150,105,0.06)',
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                background: 'rgba(5,150,105,0.06)',
                borderBottom: '1.5px solid rgba(52,211,153,0.3)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 11, fontWeight: 800, color: '#065f46',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}
            >
              <FeatherIcon icon="edit-3" size={13} />
              <span>Digital Signature</span>
            </div>
            <div
              style={{
                background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
                padding: '16px 20px',
                display: 'flex', alignItems: 'center', gap: 18,
              }}
            >
              <div style={{ background: '#fff', border: '1px solid #a7f3d0', borderRadius: 8, padding: 8 }}>
                <img
                  src={person.signature.signature}
                  alt="Signature"
                  style={{ height: 52, maxWidth: 220, objectFit: 'contain' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#059669', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FeatherIcon icon="check-circle" size={15} /> Signed & Verified
                </div>
                {person.signature.date && (
                  <div style={{ fontSize: 12, color: '#047857', marginTop: 3, fontWeight: 500 }}>
                    {fmtDate(person.signature.date)}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   INVESTIGATION SECTION
   ───────────────────────────────────────── */
const InvestigationSection = ({ investigation, token, onPreviewMedia }) => {
  const inv = investigation || {};

  const onPreviewSingle = (key) => {
    if (!key || key === 'N/A') return;
    onPreviewMedia(key, [key], 0);
  };

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 18,
        border: '1.5px solid var(--pir-violet-bd)',
        marginBottom: 20,
        boxShadow: 'var(--pir-shadow)',
        overflow: 'hidden',
      }}
    >
      {/* Card header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '18px 22px',
          background: 'linear-gradient(135deg, #061524 0%, #163b5f 60%, #3a6b98 100%)',
          position: 'relative', overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: 46, height: 46, borderRadius: 14, flexShrink: 0,
            background: 'linear-gradient(135deg,rgba(255,255,255,0.2),rgba(255,255,255,0.08))',
            border: '1.5px solid rgba(255,255,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            position: 'relative', zIndex: 1,
          }}
        >
          <FeatherIcon icon="search" size={20} color="#fff" />
        </div>
        <div style={{ flex: 1, position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Investigation Report</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>Internal Investigation Details</div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '20px 22px', background: '#f6f9fc', display: 'flex', flexDirection: 'column', gap: 0 }}>

        {/* Investigation Details */}
        <PreviewSection icon="info" title="Investigation Details">
          <PreviewField label="Occurrence Date/Time" value={fmt(inv.occurrenceDateTime)} />
          <PreviewField label="Manager Notified" value={fmt(inv.notificationToPropertyManagerDateTime)} />
          <PreviewField label="Temperature (°C)" value={inv.temperatureCelsius != null ? `${inv.temperatureCelsius}°C` : null} />
          <PreviewField label="Weather Conditions" value={(inv.weatherConditions || []).join(', ') || null} />
          <PreviewField label="Environment Conditions" value={(inv.environmentConditions || []).join(', ') || null} />
          <PreviewField label="Visibility" value={(inv.visibility || []).join(', ') || null} />
          <PreviewField label="Investigation Statement" value={display(inv.statement)} full />
        </PreviewSection>

        {/* Supporting Documents */}
        {(inv.leaseDocument || inv.carInsuranceDocument || inv.homeInsuranceDocument) && (
          <div
            style={{
              background: '#fff',
              border: '1.5px solid var(--pir-violet-bd)',
              borderRadius: 14,
              marginBottom: 16,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(22,59,95,0.06)',
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                background: 'var(--pir-violet-md)',
                borderBottom: '1.5px solid var(--pir-violet-bd)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 11, fontWeight: 800, color: 'var(--pir-indigo)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}
            >
              <FeatherIcon icon="folder" size={13} />
              <span>Supporting Documents</span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '1px',
                background: 'var(--pir-violet-bd)',
              }}
            >
              <SingleDocPreviewField label="Lease Document" fileKey={inv.leaseDocument} token={token} onClick={() => onPreviewSingle(inv.leaseDocument)} />
              <SingleDocPreviewField label="Car Insurance" fileKey={inv.carInsuranceDocument} token={token} onClick={() => onPreviewSingle(inv.carInsuranceDocument)} />
              <SingleDocPreviewField label="Home Insurance" fileKey={inv.homeInsuranceDocument} token={token} onClick={() => onPreviewSingle(inv.homeInsuranceDocument)} />
            </div>
          </div>
        )}

        {/* Attachments */}
        {inv.attachments?.length > 0 && (
          <div
            style={{
              background: '#fff',
              border: '1.5px solid var(--pir-violet-bd)',
              borderRadius: 14,
              marginBottom: 16,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(22,59,95,0.06)',
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                background: 'var(--pir-violet-md)',
                borderBottom: '1.5px solid var(--pir-violet-bd)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 11, fontWeight: 800, color: 'var(--pir-indigo)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}
            >
              <FeatherIcon icon="paperclip" size={13} />
              <span>Attachments ({inv.attachments.length})</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: 16 }}>
              {inv.attachments.map((key, ai) => (
                <AttachmentThumbnail
                  key={ai}
                  fileKey={key}
                  token={token}
                  onClick={() => onPreviewMedia(key, inv.attachments, ai)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Witnesses */}
        {(inv.witnessChoice === 'no' || (inv.witnesses?.length === 0 && inv.witnessChoice !== 'yes')) ? (
          <div
            style={{
              background: '#fff',
              border: '1.5px solid var(--pir-violet-bd)',
              borderRadius: 14,
              marginBottom: 16,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(22,59,95,0.06)',
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                background: 'var(--pir-violet-md)',
                borderBottom: '1.5px solid var(--pir-violet-bd)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 11, fontWeight: 800, color: 'var(--pir-indigo)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}
            >
              <FeatherIcon icon="users" size={13} />
              <span>Witnesses</span>
            </div>
            <div style={{ padding: '14px 18px', fontSize: 12.5, color: '#6a8ca8', fontStyle: 'italic' }}>
              No witnesses reported
            </div>
          </div>
        ) : inv.witnesses?.length > 0 ? (
          <div
            style={{
              background: '#fff',
              border: '1.5px solid var(--pir-violet-bd)',
              borderRadius: 14,
              marginBottom: 16,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(22,59,95,0.06)',
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                background: 'var(--pir-violet-md)',
                borderBottom: '1.5px solid var(--pir-violet-bd)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 11, fontWeight: 800, color: 'var(--pir-indigo)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}
            >
              <FeatherIcon icon="users" size={13} />
              <span>Witnesses ({inv.witnesses.length})</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {inv.witnesses.map((w, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '11px 18px',
                    borderTop: i === 0 ? 'none' : '1px solid var(--pir-violet-bd)',
                    background: '#fff',
                  }}
                >
                  <div
                    style={{
                      width: 26, height: 26, borderRadius: '50%',
                      background: 'linear-gradient(135deg,#163b5f,#3a6b98)',
                      color: '#fff', fontSize: 11, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13.5 }}>
                      {[w.firstName, w.lastName].filter(Boolean).join(' ') || `Witness ${i + 1}`}
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 3, fontSize: 11.5, color: '#64748b', flexWrap: 'wrap' }}>
                      {w.email && <span>✉ {w.email}</span>}
                      {w.phoneNo && <span>📞 {w.phoneNo}</span>}
                      {w.address && <span>📍 {w.address}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Investigation Completed By */}
        {inv.investigationDoneBy?.name && (
          <div
            style={{
              background: '#fff',
              border: '1.5px solid rgba(52,211,153,0.4)',
              borderRadius: 14,
              marginBottom: 16,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(5,150,105,0.06)',
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                background: 'rgba(5,150,105,0.06)',
                borderBottom: '1.5px solid rgba(52,211,153,0.3)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 11, fontWeight: 800, color: '#065f46',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}
            >
              <FeatherIcon icon="check-circle" size={13} />
              <span>Investigation Completed By</span>
            </div>
            <div style={{ padding: '14px 18px', background: '#f0fdf4', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, color: '#166534', fontWeight: 700 }}>{inv.investigationDoneBy.name}</div>
                {inv.investigationDoneBy.date && (
                  <div style={{ fontSize: 12, color: '#4ade80', marginTop: 2 }}>{fmtDate(inv.investigationDoneBy.date)}</div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   MAIN EXPORT
   ───────────────────────────────────────── */
const IncidentReportPreviewUI = ({
  formik,
  token,
  propertyTitle,
  referenceId,
  expiresAt,
  serverCanViewInvestigation,
}) => {
  const [selectedMedia, setSelectedMedia] = useState(null);

  const fv = formik?.values || {};
  const n = fv.notification || {};
  const inv = fv.investigation || {};
  const allPersons = n.personsInvolved || n.personInvolved || [];

  const { canView, canViewInvestigation, personsToShow, matchedIdx } =
    derivePermissions(token, serverCanViewInvestigation, allPersons);

  const isPersonView = personsToShow === 'matched';

  const personsToRender = isPersonView
    ? [{ person: allPersons[matchedIdx], displayIndex: matchedIdx }]
    : allPersons.map((person, idx) => ({ person, displayIndex: idx }));

  const previewMedia = useCallback(async (key, list, index) => {
    if (!token) { toast.error('Missing token'); return; }
    try {
      const data = await requestPublicPreviewUrl({ token, key });
      setSelectedMedia({
        url: data.presignedUrl,
        name: fileName(key),
        isVideo: isVideoKey(key),
        isPdf: isPdfKey(key),
        list,
        index,
      });
    } catch (err) {
      toast.error(err.message || 'Failed to load preview');
    }
  }, [token]);

  const navigateMedia = (delta) => {
    if (!selectedMedia?.list || selectedMedia.index === undefined) return;
    const newIdx = selectedMedia.index + delta;
    if (newIdx >= 0 && newIdx < selectedMedia.list.length) {
      previewMedia(selectedMedia.list[newIdx], selectedMedia.list, newIdx);
    }
  };

  if (!canView) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div
            style={{
              width: 64, height: 64, borderRadius: '50%', background: '#fee2e2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            }}
          >
            <FeatherIcon icon="lock" size={28} color="#ef4444" />
          </div>
          <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700 }}>Access Denied</h3>
          <p style={{ color: '#64748b', fontSize: 14 }}>You do not have permission to view this report.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pir-wrap">
      {/* ── Page header ── */}
      <div className="pir-header">
        <div className="pir-header-glow" />
        <div className="pir-header-inner">
          <div className="pir-header-top">
            <div>
              <h1 className="pir-header-title">Incident Report</h1>
              <p className="pir-header-sub">
                {propertyTitle ? `Property: ${propertyTitle}` : 'Preview Mode — Read Only'}
              </p>
            </div>
            <img src="/images/rentdigicare-logo.svg" alt="RentDigiCare" className="pir-header-logo" />
          </div>

          <div className="pir-badge-row">
            {referenceId && (
              <span className="pir-badge">
                <FeatherIcon icon="hash" size={11} /> {referenceId}
              </span>
            )}
            {expiresAt && (
              <span className="pir-badge pir-badge-amber">
                <FeatherIcon icon="clock" size={11} />
                Due:{' '}
                {new Date(expiresAt).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                })}
              </span>
            )}
            {n?.managerReporting && (
              <span className="pir-badge">
                <FeatherIcon icon="users" size={11} /> {n.managerReporting}
              </span>
            )}
            {n?.managerEmail && (
              <span className="pir-badge">
                <FeatherIcon icon="mail" size={11} /> {n.managerEmail}
              </span>
            )}
            <span className="pir-badge pir-badge-green">
              <FeatherIcon icon="eye" size={11} />
              {isPersonView ? 'Your Submission' : 'Full Report Preview'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="pir-content">
        {personsToRender.length === 0 && (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 10,
              padding: '36px 16px', color: '#94a3b8',
              border: '1.5px dashed var(--pir-violet-bd)',
              borderRadius: 14, background: 'rgba(22,59,95,0.03)',
            }}
          >
            <FeatherIcon icon="user-x" size={28} color="#cbd5e1" />
            <span style={{ fontSize: 13 }}>No persons involved listed on this report.</span>
          </div>
        )}

        {personsToRender.map(({ person, displayIndex }) => (
          <PersonCard
            key={displayIndex}
            person={person}
            index={displayIndex}
            token={token}
            onPreviewMedia={previewMedia}
          />
        ))}

        {/* Investigation — visible only when permitted */}
        {canViewInvestigation && (
          <InvestigationSection
            investigation={inv}
            token={token}
            onPreviewMedia={previewMedia}
          />
        )}

        {/* ── Notification Recipients intentionally removed from public preview ── */}
      </div>

      <MediaPreviewModal
        isOpen={!!selectedMedia}
        toggle={() => setSelectedMedia(null)}
        media={selectedMedia}
        onPrev={selectedMedia?.index > 0 ? () => navigateMedia(-1) : null}
        onNext={
          selectedMedia && selectedMedia.list && selectedMedia.index < selectedMedia.list.length - 1
            ? () => navigateMedia(1)
            : null
        }
      />
    </div>
  );
};

export { IncidentReportPreviewUI };